import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"
import { formatCents } from "./currency.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.textile_picker.${key}`, options)

// Below this stock a size is shown as low (`.qstock--low` of the mockup)
const LOW_STOCK = 15

const RESULT = "grid w-full cursor-pointer grid-cols-[44px_minmax(0,1fr)] items-center gap-2.5 rounded-xl border " +
  "border-transparent p-2 text-left hover:bg-erp-surface-soft aria-selected:border-erp-black " +
  "aria-selected:bg-erp-surface-soft focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-erp-black"
const GROUP = "px-2 pt-2.5 pb-1 text-[11px] font-extrabold uppercase tracking-[0.07em] text-erp-muted"
const NOTE = "px-2 py-4 text-[13px] text-erp-muted"
const ALERT = {
  warn: "flex items-start gap-2.5 rounded-erp-field bg-erp-st-pending-bg px-3.5 py-3 text-[13px] leading-[1.45] text-erp-st-pending-fg",
  info: "flex items-start gap-2.5 rounded-erp-field bg-[#EEF1F7] px-3.5 py-3 text-[13px] leading-[1.45] text-erp-st-sent-fg"
}
const COMPATIBILITY_CHIP = {
  allowed: "bg-erp-st-won-bg text-erp-st-won-fg",
  approval_required: "bg-erp-st-pending-bg text-erp-st-pending-fg",
  unspecified: "bg-[#F2EEE3] text-erp-muted",
  incompatible: "bg-[#ECE9E1] text-[#6B675C] line-through"
}
const COMPATIBILITY_ORDER = ["incompatible", "approval_required", "unspecified", "allowed"]

// "Ajouter un textile" window (_textile_modal): catalogue products (search, product sheet, colour × size grid with
// stocks) or a garment the customer brings in. erp--estimate-editor opens it with the `textile-picker:open` event
// (detail: mode "add" or "replace", tab "catalogue" or "customer"); confirming dispatches `items-selected`
// (catalogue colourways) or `custom-selected`, and closing without confirming dispatches `cancelled`, all three
// from the <dialog> so they bubble to the window.
// Connects to data-controller="erp--textile-picker"
export default class extends Controller {
  static targets = [
    "tabButton", "panel",
    "searchInput", "supplierFilter", "categoryFilter", "searchResults", "placeholder", "matrix",
    "garmentInput", "colorInput", "commentInput", "sizeInput",
    "grid", "summary", "submit", "manualBody", "manualErrors", "manualExisting", "manualReactivate",
    "alreadyMarked"
  ]

  static values = {
    searchUrl: String, // products search endpoint
    matrixUrl: String, // base path of /products/:id/variants_matrix
    manualUrl: String,
    customerId: Number,
    quantityCellClasses: String,
    gridLabels: Object // "Tarif direct appliqué" by customer type
  }

  connect() {
    this.dialog = this.element.querySelector("dialog")
    this.title = this.dialog.querySelector("h2")
    this._mode = "add"
    this._matrix = null
    this._confirmed = false
    this._onOpen = (event) => this.#open(event.detail || {})
    this._onClose = () => this.#closed()
    document.addEventListener("textile-picker:open", this._onOpen)
    this.dialog.addEventListener("close", this._onClose)
    this.#activateTab("catalogue")
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    this._searchRequest?.abort()
    this._matrixRequest?.abort()
    document.removeEventListener("textile-picker:open", this._onOpen)
    this.dialog.removeEventListener("close", this._onClose)
  }

  // ── Tabs ─────────────────────────────────────────────────────

  switchTab(event) {
    this.#showTab(event.currentTarget.dataset.tab)
  }

  navigateTab(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return

    event.preventDefault()
    const tabs = this.tabButtonTargets
    const current = tabs.indexOf(event.currentTarget)
    const next = event.key === "Home" ? 0
      : event.key === "End" ? tabs.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length
    this.#showTab(tabs[next].dataset.tab)
    tabs[next].focus()
  }

  // A window opened on the customer tab loads its catalogue list the first time the catalogue shows
  #showTab(tab) {
    this.#activateTab(tab)
    if (tab === "catalogue" && !this.searchResultsTarget.hasChildNodes()) this.#performSearch()
  }

  // ── Catalogue: search ────────────────────────────────────────

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.#performSearch(), 250)
  }

  searchKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "Enter") return

    const first = this.#resultButtons()[0]
    if (!first) return
    event.preventDefault()
    first.focus()
  }

  resultsKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    event.preventDefault()
    const buttons = this.#resultButtons()
    const index = buttons.indexOf(document.activeElement)
    if (event.key === "ArrowDown") buttons[Math.min(index + 1, buttons.length - 1)]?.focus()
    else if (index <= 0) this.searchInputTarget.focus()
    else buttons[index - 1].focus()
  }

  async selectProduct(event) {
    event.preventDefault()
    await this.#loadProduct(event.currentTarget.dataset.productId)
  }

  async #loadProduct(productId) {
    this.#resultButtons().forEach(button => button.setAttribute("aria-selected", String(button.dataset.productId === productId)))
    this._matrix = null
    this.#showPlaceholder(t("loading"))
    this.refreshFooter()

    this._matrixRequest?.abort()
    const request = this._matrixRequest = new AbortController()
    try {
      const response = await fetch(this.#productMatrixUrl(productId), { headers: { Accept: "application/json" }, signal: request.signal })
      if (request !== this._matrixRequest) return
      if (!response.ok) throw new Error(`variants_matrix ${response.status}`)

      this._matrix = await response.json()
      this.#renderDetail()
      this.refreshFooter()
      this.matrixTarget.querySelector("input[data-variant-id]:not([disabled])")?.focus()
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[textile-picker]", error)
      this.#showPlaceholder(t("load_error"), true)
    }
  }

  #productMatrixUrl(productId) {
    const url = new URL(`${this.matrixUrlValue}/${productId}/variants_matrix`, window.location.origin)
    if (this.customerIdValue > 0) url.searchParams.set("customer_id", this.customerIdValue)
    return url
  }

  // ── Catalogue: colour × size grid ────────────────────────────

  quantityChanged(event) {
    const input = event.target.closest("input[data-variant-id]")
    if (!input) return

    this.#markQuantity(input)
    if (this.matrixTarget.querySelector("[data-only-filled]")?.checked) this.#filterFilledColours(true)
    this.#refreshStockAlert()
    this.refreshFooter()
  }

  toolChanged(event) {
    if (event.target.matches("[data-only-filled]")) this.#filterFilledColours(event.target.checked)
  }

  #filterFilledColours(onlyFilled) {
    this.matrixTarget.querySelectorAll("tbody tr[data-colour-row]").forEach(row => {
      row.hidden = onlyFilled && !row.hasAttribute("data-has-quantity")
    })
  }

  // Arrows and Enter move between the quantity cells
  gridKeydown(event) {
    const input = event.target.closest("input[data-variant-id]")
    if (!input || !["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return

    const inputs = [...this.matrixTarget.querySelectorAll("input[data-variant-id]:not([disabled])")]
      .filter(candidate => !candidate.closest("tr").hidden)
    const index = inputs.indexOf(input)
    const row = Number(input.dataset.row)
    const column = Number(input.dataset.column)
    let target
    if (event.key === "ArrowRight" || event.key === "Enter") target = inputs[index + 1]
    else if (event.key === "ArrowLeft") target = inputs[index - 1]
    else if (event.key === "ArrowDown") target = inputs.find(candidate => Number(candidate.dataset.column) === column && Number(candidate.dataset.row) > row)
    else target = inputs.findLast(candidate => Number(candidate.dataset.column) === column && Number(candidate.dataset.row) < row)

    event.preventDefault()
    if (target) {
      target.focus()
      target.select()
    }
  }

  // ── Footer ───────────────────────────────────────────────────

  refreshFooter() {
    const { valid, message } = this._activeTab === "manual" ? { valid: false, message: t("manual_hint") }
      : this._activeTab === "customer" ? this.#customerState() : this.#catalogueState()
    this.summaryTarget.textContent = message
    this.summaryTarget.toggleAttribute("data-valid", valid)
    this.submitTarget.disabled = !valid
  }

  // A save in progress is no reason to wait: erp--estimate-editor queues the new lines until it ends
  confirm() {
    if (this._activeTab === "customer") {
      if (!this.#customerState().valid) return
      const garment = this.garmentInputTargets.find(input => input.checked)
      this.dispatch("custom-selected", {
        target: this.dialog,
        detail: {
          garment_type: garment.value,
          garment_label: garment.closest("label")?.textContent.trim() || "",
          color_name: this.colorInputTarget.value.trim(),
          comment: this.commentInputTarget.value.trim(),
          sizes: this.#customSizes(),
          already_marked: this.#alreadyMarked()
        }
      })
    } else {
      if (!this.#catalogueState().valid) return
      this.dispatch("items-selected", {
        target: this.dialog,
        detail: { colours: this.#catalogueColours(), already_marked: this.#alreadyMarked() }
      })
    }

    this._confirmed = true
    this.dialog.close()
  }

  // ── Opening and closing ──────────────────────────────────────

  #open({ mode, tab, alreadyMarked }) {
    this.#reset()
    if (this.hasAlreadyMarkedTarget) this.alreadyMarkedTarget.checked = alreadyMarked === true
    const customerField = document.querySelector("[name='estimate[customer_id]']")
    if (customerField) this.customerIdValue = parseInt(customerField.value) || 0
    this.gridTarget.textContent = this.gridLabelsValue[customerField?.dataset.customerType || "direct"] || ""
    this.gridTarget.hidden = !(this.customerIdValue > 0)
    this._mode = mode === "replace" ? "replace" : "add"
    this.title.textContent = t(this._mode === "replace" ? "replace_title" : "add_title")
    this.submitTarget.textContent = t(this._mode === "replace" ? "replace" : "add")
    this.#activateTab(tab === "customer" ? "customer" : "catalogue")
    if (!this.dialog.open) this.dialog.showModal()

    if (this._activeTab === "customer") {
      this.garmentInputTargets[0]?.focus()
    } else {
      this.searchInputTarget.focus()
      this.#performSearch()
    }
  }

  #closed() {
    if (!this._confirmed) this.dispatch("cancelled", { target: this.dialog })
    this._confirmed = false
    this.#reset()
  }

  #reset() {
    clearTimeout(this._debounceTimer)
    this._searchRequest?.abort()
    this._matrixRequest?.abort()
    this._matrix = null
    this.searchInputTarget.value = ""
    this.supplierFilterTarget.value = ""
    this.categoryFilterTarget.value = ""
    this.searchResultsTarget.replaceChildren()
    this.#showPlaceholder()
    this.garmentInputTargets.forEach(input => { input.checked = false })
    this.colorInputTarget.value = ""
    this.commentInputTarget.value = ""
    this.sizeInputTargets.forEach(input => {
      input.value = ""
      input.removeAttribute("data-filled")
    })
    if (this.hasAlreadyMarkedTarget) this.alreadyMarkedTarget.checked = false
    this.#resetManualForm()
    this.refreshFooter()
  }

  #alreadyMarked() {
    return this.hasAlreadyMarkedTarget && this.alreadyMarkedTarget.checked
  }

  #resetManualForm() {
    if (!this.hasManualBodyTarget) return

    this._manualCycle = Symbol()
    this.manualBodyTarget.hidden = true
    this.manualBodyTarget.querySelectorAll("input, select, textarea").forEach(input => this.#resetManualField(input))
    this.manualEdited()
    this.#syncManualForm()
  }

  #resetManualField(input) {
    if (input.tagName === "SELECT") {
      Array.from(input.options).forEach(option => { option.selected = option.defaultSelected })
      return
    }

    input.value = input.defaultValue
    if (input.type === "checkbox") input.checked = input.defaultChecked
  }

  #syncManualForm() {
    if (!this.hasManualBodyTarget) return

    this.manualBodyTarget.querySelector("[data-controller='erp--manual-article-form']")
      ?.dispatchEvent(new CustomEvent("manual-article:activate"))
  }

  #activateTab(tab) {
    this._activeTab = tab
    this.tabButtonTargets.forEach(button => {
      const active = button.dataset.tab === tab
      button.setAttribute("aria-selected", String(active))
      button.tabIndex = active ? 0 : -1
    })
    this.panelTargets.forEach(panel => { panel.hidden = panel.dataset.tab !== tab })
    this.#syncManualForm()
    this.refreshFooter()
  }

  manualEdited() {
    this.manualErrorsTarget.textContent = ""
    this.#clearManualDuplicateLinks()
  }

  #clearManualDuplicateLinks() {
    this._existingManualId = null
    this.manualExistingTarget.hidden = true
    this.manualReactivateTarget.hidden = true
    this.manualReactivateTarget.removeAttribute("href")
  }

  async createManual(event) {
    if (this._creatingManual) return
    const fields = [...this.manualBodyTarget.querySelectorAll("input, select, textarea")].filter(input => !input.disabled)
    if (!this.#manualFieldsValid(fields)) return

    const button = event.currentTarget
    this._creatingManual = true
    button.disabled = true
    this.manualErrorsTarget.textContent = t("manual_saving")
    this.#clearManualDuplicateLinks()
    const body = this.#manualArticleBody(fields)
    const cycle = this._manualCycle
    try {
      const { response, data } = await this.#postManualArticle(body)
      if (cycle !== this._manualCycle) return

      if (!response.ok) {
        this.#showManualCreationErrors(data)
        return
      }
      this.#resetManualForm()
      if (this.dialog.open) {
        this.#showTab("catalogue")
        await this.#loadProduct(data.product_id)
      }
    } catch {
      if (cycle === this._manualCycle) this.manualErrorsTarget.textContent = t("manual_failed")
    } finally {
      button.disabled = false
      this._creatingManual = false
    }
  }

  #manualFieldsValid(fields) {
    if (fields.some(input => !input.reportValidity())) return false
    const kind = fields.find(input => input.name === "manual_article[article_kind]")?.value
    if (kind !== "textile") return true

    const hasSize = fields.some(input => input.name === "manual_article[size_ids][]" && input.checked)
    if (hasSize) return true

    this.manualErrorsTarget.textContent = t("manual_no_sizes")
    return false
  }

  #manualArticleBody(fields) {
    const body = new FormData()
    fields.forEach(input => {
      if (!input.name) return
      if (input.type === "checkbox" && !input.checked) return
      body.append(input.name, input.value)
    })
    return body
  }

  async #postManualArticle(body) {
    const response = await fetch(this.manualUrlValue, {
      method: "POST", body,
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" }
    })
    return { response, data: await response.json() }
  }

  #showManualCreationErrors(data) {
    this.manualErrorsTarget.textContent = (data.errors || [t("manual_failed")]).join(" · ")
    this._existingManualId = data.existing_product_id
    this.manualExistingTarget.hidden = !this._existingManualId
    const reactivationLink = this.manualReactivateTarget
    reactivationLink.hidden = !data.reactivation_url
    if (data.reactivation_url) reactivationLink.href = data.reactivation_url
    else reactivationLink.removeAttribute("href")
  }

  async useExisting() {
    if (!this._existingManualId) return
    this.#showTab("catalogue")
    await this.#loadProduct(this._existingManualId)
  }

  // ── Search results ───────────────────────────────────────────

  async #performSearch() {
    const query = this.searchInputTarget.value.trim()
    const filtered = Boolean(this.supplierFilterTarget.value || this.categoryFilterTarget.value)
    const searching = query.length >= 2 || filtered
    if (!searching && !(this.customerIdValue > 0)) return this.#renderNote(t("type_more"))

    this._searchRequest?.abort()
    const request = this._searchRequest = new AbortController()
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      if (query.length >= 2) url.searchParams.set("q", query)
      if (this.supplierFilterTarget.value) url.searchParams.set("supplier", this.supplierFilterTarget.value)
      if (this.categoryFilterTarget.value) url.searchParams.set("category_id", this.categoryFilterTarget.value)
      if (this.customerIdValue > 0) url.searchParams.set("customer_id", this.customerIdValue)

      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: request.signal })
      if (request !== this._searchRequest) return
      if (!response.ok) throw new Error(`search ${response.status}`)

      this.#renderResults(await response.json(), { query, searching, filtered })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[textile-picker]", error)
      this.#renderNote(t("load_error"), true)
    }
  }

  // Products this customer already ordered come first, under their own heading
  #renderResults(products, { query, searching, filtered }) {
    if (products.length === 0) {
      if (!searching) return this.#renderNote(t("type_more"))
      return this.#renderNote(query.length >= 2 ? t("no_results", { query }) : t("no_filtered_results"))
    }

    const selectedId = this._matrix?.product_id?.toString()
    const ordered = products.filter(product => product.ordered)
    const others = products.filter(product => !product.ordered)
    const nodes = []
    if (ordered.length > 0) nodes.push(this.#groupHeading(t("ordered_group")), ...ordered.map(product => this.#resultButton(product, selectedId)))
    if (others.length > 0) nodes.push(this.#groupHeading(t("results_group", { count: others.length })), ...others.map(product => this.#resultButton(product, selectedId)))
    if (!searching) nodes.push(this.#note(t("type_more")))
    this.searchResultsTarget.replaceChildren(...nodes)
  }

  #resultButton(product, selectedId) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = RESULT
    button.setAttribute("role", "option")
    button.setAttribute("aria-selected", String(product.id.toString() === selectedId))
    button.dataset.productId = product.id
    button.dataset.action = "click->erp--textile-picker#selectProduct"

    const thumb = this.#thumbnail(product.image_url, "size-11 rounded-[10px] bg-[#F2EEE3]", "text-2xl")
    const text = document.createElement("span")
    text.className = "flex min-w-0 flex-col gap-0.5"
    text.append(
      this.#element("b", "truncate text-[13px] font-extrabold", [product.brand, product.name].filter(Boolean).join(" ")),
      this.#element("small", "truncate text-xs text-erp-muted", [product.reference, product.supplier_label || product.supplier].filter(Boolean).join(" · ")),
      this.#element("span", "text-xs font-bold", t("variants", { count: product.variant_count }))
    )
    button.append(thumb, text)
    return button
  }

  #resultButtons() {
    return [...this.searchResultsTarget.querySelectorAll("button[data-product-id]")]
  }

  #groupHeading(text) {
    return this.#element("div", GROUP, text)
  }

  #note(text, error = false) {
    const note = this.#element("p", error ? `${NOTE} text-erp-danger` : NOTE, text)
    if (error) note.setAttribute("role", "alert")
    return note
  }

  #renderNote(text, error = false) {
    this.searchResultsTarget.replaceChildren(this.#note(text, error))
  }

  // ── Product sheet ────────────────────────────────────────────

  #showPlaceholder(text = null, error = false) {
    const detail = this.placeholderTarget.querySelector("[data-placeholder-text]")
    detail.dataset.defaultText ||= detail.textContent
    detail.textContent = text || (this.customerIdValue > 0 ? detail.dataset.defaultText : t("select_customer_hint"))
    detail.classList.toggle("text-erp-danger", error)
    this.placeholderTarget.hidden = false
    this.matrixTarget.replaceChildren()
    this.matrixTarget.classList.replace("flex", "hidden")
  }

  #renderDetail() {
    const matrix = this._matrix
    const sizes = matrix.sizes || []
    const variants = matrix.colors.flatMap(colour => colour.sizes || [])
    const prices = new Set(variants.map(variant => variant.customer_price_cents))
    const stockDates = new Set(variants.map(variant => variant.stock_updated_at))
    const commonPrice = variants.length > 0 && !prices.has(null) && !prices.has(undefined) && prices.size === 1 ? [...prices][0] : null
    const commonStockDate = variants.length > 0 && stockDates.size === 1 ? [...stockDates][0] || null : null
    this._commonPrice = commonPrice

    const nodes = [this.#productHead(matrix, commonPrice, commonStockDate)]
    if (sizes.length === 0 || matrix.colors.length === 0) {
      nodes.push(this.#note(t("no_variants")))
    } else {
      nodes.push(this.#alerts(variants, sizes, matrix), this.#compatibility(matrix.compatibility), this.#tools(matrix),
        this.#grid(matrix, sizes, { showPrice: commonPrice == null, showStockDate: commonStockDate == null }))
    }

    this.placeholderTarget.hidden = true
    this.matrixTarget.replaceChildren(...nodes.filter(Boolean))
    this.matrixTarget.classList.replace("hidden", "flex")
    this.#refreshStockAlert()
  }

  #productHead(matrix, commonPrice, commonStockDate) {
    const head = this.#element("div", "flex flex-wrap items-start gap-4")
    const firstImage = matrix.colors.find(colour => colour.image_url)?.image_url
    const text = this.#element("div", "flex min-w-0 flex-1 flex-col gap-1")
    text.append(
      this.#element("h3", "text-lg font-extrabold", matrix.product_name),
      this.#element("p", "text-[13px] text-erp-muted", [matrix.reference, matrix.brand, matrix.supplier_label || matrix.supplier, matrix.category].filter(Boolean).join(" · "))
    )
    if (commonStockDate) {
      const stockDate = this.#element("p", "text-xs text-erp-muted", t("stock_as_of", { date: this.#formatDate(commonStockDate) }))
      stockDate.dataset.textilePickerStockSummary = ""
      text.append(stockDate)
    }

    const price = this.#element("div", "flex flex-col gap-0.5 text-right max-[900px]:text-left")
    if (commonPrice != null) {
      const amount = this.#element("strong", "text-[22px] font-extrabold tabular-nums", t("client_price", { price: this.#money(commonPrice) }))
      amount.dataset.textilePickerPriceSummary = ""
      price.append(amount, this.#element("span", "text-xs text-erp-muted", t("per_piece")))
    } else if (this.customerIdValue > 0) {
      price.append(this.#element("span", "text-xs font-bold text-erp-muted", t("price_varies")))
    }

    head.append(this.#thumbnail(firstImage, "size-[72px] rounded-2xl border border-erp-line bg-erp-surface-soft", "text-4xl"), text, price)
    return head
  }

  #alerts(variants, sizes, matrix) {
    const alerts = this.#element("div", "flex flex-col gap-2")
    const blocked = sizes.filter(size => {
      const sizeVariants = variants.filter(variant => variant.size === size)
      return sizeVariants.length > 0 && sizeVariants.every(variant => variant.size_mapping_status !== "mapped")
    })
    if (blocked.length > 0 && blocked.length === sizes.length) {
      alerts.append(this.#alert("warn", t("unmapped_all_title"), t("unmapped_all_text")))
    } else if (blocked.length > 0) {
      const labels = blocked.map(size => `« ${matrix.size_labels?.[size] || size} »`).join(", ")
      alerts.append(this.#alert("info", null, t("unmapped_some", { sizes: labels })))
    }

    this._stockAlert = this.#element("div", "contents")
    alerts.append(this._stockAlert)
    return alerts
  }

  #alert(variant, title, text) {
    const alert = this.#element("div", ALERT[variant])
    alert.dataset.variant = variant
    const icon = this.#element("i", `lni lni-${variant === "warn" ? "info-triangle" : "info-circle"} mt-px shrink-0 text-base leading-none`)
    icon.setAttribute("aria-hidden", "true")
    const body = this.#element("span")
    if (title) body.append(this.#element("b", "font-bold", title), " ")
    body.append(text)
    alert.append(icon, body)
    return alert
  }

  #refreshStockAlert() {
    if (!this._stockAlert) return

    const over = [...this.matrixTarget.querySelectorAll("input[data-variant-id][data-over]")].map(input => t("over_stock_item", {
      colour: input.dataset.colorName,
      size: input.dataset.canonicalSize || input.dataset.size,
      quantity: parseInt(input.value) || 0,
      stock: input.dataset.stock
    }))
    this._stockAlert.replaceChildren(...(over.length === 0 ? [] : [
      this.#alert("warn", t("over_stock_title", { count: over.length }), `${over.join(", ")}. ${t("over_stock_text")}`)
    ]))
  }

  // Techniques this product accepts, one chip each, then the technique × zone rules
  #compatibility(summary = {}) {
    const rules = Array.isArray(summary?.rules) ? summary.rules : []
    if (rules.length === 0) return null

    const details = this.#element("details", "group/compat rounded-erp-field border border-erp-line px-3.5")
    details.dataset.textilePickerCompatibility = ""
    const title = this.#element("summary", "flex h-11 cursor-pointer list-none items-center justify-between gap-3 text-[13px] font-extrabold [&::-webkit-details-marker]:hidden", t("compatibility_title"))
    const chevron = this.#element("i", "lni lni-chevron-down text-xs transition-transform group-open/compat:rotate-180")
    chevron.setAttribute("aria-hidden", "true")
    title.append(chevron)

    const byTechnique = new Map()
    rules.forEach(rule => byTechnique.set(rule.technique, [...(byTechnique.get(rule.technique) || []), rule.status]))
    const chips = this.#element("ul", "mb-3 flex flex-wrap gap-1.5")
    byTechnique.forEach((statuses, technique) => {
      const status = statuses.includes("allowed") ? "allowed"
        : statuses.includes("approval_required") ? "approval_required"
          : statuses.every(value => value === "incompatible") ? "incompatible" : "unspecified"
      const chip = this.#element("li", `rounded-full px-2.5 py-1 text-xs font-bold ${COMPATIBILITY_CHIP[status]}`, technique)
      chip.title = t(`compatibility_status.${status}`)
      chips.append(chip)
    })

    const list = this.#element("ul", "mb-3 flex flex-col gap-1.5 text-xs")
    rules.forEach(rule => {
      const item = this.#element("li", "rounded-lg bg-erp-surface-soft px-3 py-2")
      item.append(this.#element("b", "font-bold", `${rule.technique} · ${rule.zone} — ${t(`compatibility_status.${rule.status}`)}`))
      const metadata = [
        rule.max_width_mm || rule.max_height_mm ? t("max_dimensions", { width: rule.max_width_mm || "—", height: rule.max_height_mm || "—" }) : null,
        rule.inherited ? t("inherited_rule") : null,
        rule.operator_note || null
      ].filter(Boolean)
      if (metadata.length > 0) item.append(this.#element("span", "block text-erp-muted", metadata.join(" · ")))
      list.append(item)
    })

    details.append(title, chips, list)
    return details
  }

  #tools(matrix) {
    const tools = this.#element("div", "flex flex-wrap items-center gap-2.5")
    if (matrix.colors.length > 2) {
      const toggle = this.#element("label", "inline-flex h-[38px] cursor-pointer items-center gap-2.5 rounded-full border border-erp-line-strong bg-erp-surface px-4 text-xs font-bold")
      const input = this.#element("input", "relative h-5 w-[34px] cursor-pointer appearance-none rounded-full bg-[#E0D9C6] transition-colors checked:bg-erp-black after:absolute after:top-0.5 after:left-0.5 after:size-4 after:rounded-full after:bg-erp-surface after:transition-transform checked:after:translate-x-3.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black")
      input.type = "checkbox"
      input.dataset.onlyFilled = ""
      toggle.append(input, t("only_filled"))
      tools.append(toggle)
    }

    if (matrix.manual_entry) return tools

    const legend = this.#element("div", "ml-auto flex flex-wrap gap-3.5 text-xs text-erp-muted")
    const over = this.#element("span", "inline-flex items-center gap-1.5")
    over.append(this.#element("i", "inline-block size-3 rounded border border-[#D98A2B] bg-erp-st-pending-bg"), t("legend_over"))
    const unmapped = this.#element("span", "inline-flex items-center gap-1.5")
    unmapped.append(this.#element("i", "inline-block size-3 rounded border border-dashed border-erp-line-dashed"), t("legend_unmapped"))
    legend.append(over, unmapped)
    tools.append(legend)
    return tools
  }

  #grid(matrix, sizes, options) {
    const table = this.#element("table", "w-full border-separate border-spacing-0 text-[13px]")
    const headRow = this.#element("tr")
    const corner = this.#element("th", "sticky top-0 left-0 z-20 border-b border-erp-line bg-[#FBF8EF] py-2.5 pr-1.5 pl-3.5 text-left text-xs font-extrabold", t(matrix.manual_entry && matrix.article_kind === "object" ? "article" : "colourway"))
    corner.scope = "col"
    headRow.append(corner)
    sizes.forEach(size => {
      const th = this.#element("th", "sticky top-0 z-10 border-b border-erp-line bg-[#FBF8EF] px-1.5 py-2.5 text-xs font-extrabold whitespace-nowrap", matrix.size_labels?.[size] || size)
      th.scope = "col"
      headRow.append(th)
    })
    const thead = this.#element("thead")
    thead.append(headRow)

    const tbody = this.#element("tbody")
    matrix.colors.forEach((colour, rowIndex) => tbody.append(this.#colourRow(matrix, colour, sizes, { ...options, rowIndex })))
    table.append(thead, tbody)

    const wrap = this.#element("div", "overflow-auto rounded-2xl border border-erp-line")
    wrap.tabIndex = 0
    wrap.setAttribute("role", "region")
    wrap.setAttribute("aria-label", t("matrix_scroll_region"))
    wrap.append(table)
    return wrap
  }

  #colourRow(matrix, colour, sizes, options) {
    const row = this.#element("tr", "group/row")
    row.dataset.colourRow = ""
    row.dataset.compatibility = JSON.stringify(colour.compatibility || {})
    row.dataset.hexes = JSON.stringify(this.#colourHexes(colour))
    row.append(this.#colourLabel(matrix, colour))

    const bySize = new Map()
    ;(colour.sizes || []).forEach(variant => bySize.set(variant.size, [...(bySize.get(variant.size) || []), variant]))
    sizes.forEach((size, columnIndex) => {
      const cell = this.#element("td", "border-b border-[#F4F0E6] px-1.5 py-2 text-center align-top")
      const sizeVariants = bySize.get(size) || []
      if (sizeVariants.length === 0) {
        cell.append(this.#element("span", "text-erp-muted/50", "—"))
      } else {
        const stack = this.#element("div", "flex flex-col items-center gap-2")
        sizeVariants.forEach(variant => stack.append(this.#variantCell(matrix, colour, variant, { ...options, columnIndex })))
        cell.append(stack)
      }
      row.append(cell)
    })
    return row
  }

  #colourHexes(colour) {
    return Array.isArray(colour.hexes) && colour.hexes.length > 0 ? colour.hexes : [colour.hex].filter(Boolean)
  }

  #colourLabel(matrix, colour) {
    const label = this.#element("th", "sticky left-0 z-10 min-w-[170px] border-b border-[#F4F0E6] bg-erp-surface py-2 pr-1.5 pl-3.5 text-left align-top group-data-has-quantity/row:shadow-[inset_3px_0_0_var(--color-erp-accent)]")
    label.scope = "row"
    const details = this.#element("div", "flex min-w-0 flex-col items-start gap-1")
    details.dataset.textilePickerColourDetails = ""
    const main = this.#element("div", "flex items-center gap-2 font-bold")
    main.dataset.textilePickerColourMain = ""
    this.#colourHexes(colour).forEach(hex => {
      const dot = this.#element("span", "inline-block size-2.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.15)]")
      dot.style.backgroundColor = hex
      main.append(dot)
    })
    main.append(this.#element("span", "", matrix.manual_entry && matrix.article_kind === "object" ? matrix.product_name : colour.name))
    details.append(main)
    const compatibility = this.#colourCompatibility(colour.compatibility)
    if (compatibility) details.append(compatibility)
    label.append(details)
    return label
  }

  #colourCompatibility(summary = {}) {
    const counts = summary?.counts || {}
    const statuses = COMPATIBILITY_ORDER.filter(status => Number(counts[status]) > 0)
    if (statuses.length === 0) return null

    const wrap = this.#element("span", "flex flex-wrap gap-1")
    wrap.dataset.textilePickerColourCompatibility = ""
    statuses.forEach(status => {
      wrap.append(this.#element("span", `rounded-full px-1.5 py-0.5 text-[10px] font-bold no-underline ${COMPATIBILITY_CHIP[status].replace("line-through", "")}`,
        t(`compatibility.${status}`, { count: counts[status] })))
    })
    return wrap
  }

  // One quantity cell: an input with the stock under it, or a locked cell for a size the catalogue cannot order
  #variantCell(matrix, colour, variant, options) {
    const cell = this.#element("div", "flex flex-col items-center gap-[3px]")
    const visibleSize = this.#visibleVariantSize(variant)

    if (variant.size_mapping_status !== "mapped") {
      this.#appendUnmappedSize(cell, variant, visibleSize)
      return cell
    }

    const stock = Number(variant.stock) || 0
    const input = this.#variantQuantityInput(matrix, colour, variant, { ...options, visibleSize, stock })
    cell.append(input)
    this.#appendVariantDetails(cell, input, variant, options)
    cell.append(this.#variantStockLabel(matrix, stock))
    return cell
  }

  #visibleVariantSize(variant) {
    if (!variant.canonical_size || !variant.size) return variant.canonical_size || variant.size

    return variant.canonical_size !== variant.size ? `${variant.canonical_size} (${variant.size})` : variant.canonical_size
  }

  #appendUnmappedSize(cell, variant, visibleSize) {
    const ambiguous = variant.size_mapping_status === "ambiguous"
    const lock = this.#element("span", "flex h-[38px] w-[58px] cursor-help items-center justify-center rounded-[10px] border border-dashed border-erp-line-dashed bg-[repeating-linear-gradient(45deg,transparent_0_5px,#F4F0E6_5px_6px)] text-[#A39C8A]")
    lock.title = t(ambiguous ? "size_ambiguous_hint" : "size_unmapped_hint", { size: visibleSize })
    lock.setAttribute("role", "img")
    lock.setAttribute("aria-label", lock.title)
    const icon = this.#element("i", "lni lni-locked-1 text-sm")
    icon.setAttribute("aria-hidden", "true")
    lock.append(icon)
    cell.append(lock, this.#element("span", "text-[11px] text-erp-muted", t(ambiguous ? "size_ambiguous" : "size_unmapped")))
  }

  #variantQuantityInput(matrix, colour, variant, { visibleSize, stock, rowIndex, columnIndex }) {
    const input = this.#element("input", this.quantityCellClassesValue)
    input.type = "number"
    input.min = "0"
    input.inputMode = "numeric"
    input.placeholder = "0"
    Object.assign(input.dataset, {
      variantId: variant.variant_id,
      reference: variant.reference || "",
      colorName: colour.name,
      size: variant.size,
      ...this.#catalogueQuantityData(matrix, colour),
      ...this.#sizeQuantityData(variant),
      supplierPriceCents: variant.supplier_price_cents ?? "",
      customerPriceCents: variant.customer_price_cents ?? "",
      imageUrl: colour.image_url || "",
      stockUpdatedAt: variant.stock_updated_at || "",
      stock,
      row: rowIndex,
      column: columnIndex
    })
    input.setAttribute("aria-label", matrix.manual_entry
      ? t("manual_quantity_for", { article: [matrix.product_name, colour.name, visibleSize].filter(Boolean).join(" · ") })
      : t("quantity_for", { colour: colour.name, size: visibleSize, stock }))
    return input
  }

  #catalogueQuantityData(matrix, colour) {
    return {
      productId: matrix.product_id,
      productName: [matrix.brand, matrix.product_name].filter(Boolean).join(" "),
      supplier: matrix.supplier || "",
      manualEntry: matrix.manual_entry ? "true" : "false",
      articleDetails: matrix.description || "",
      productColorwayId: colour.product_colorway_id || "",
      pricingClass: colour.pricing_class || "color"
    }
  }

  #sizeQuantityData(variant) {
    return {
      canonicalSizeId: variant.canonical_size_id || "",
      canonicalSize: variant.canonical_size || variant.size,
      sizeMappingStatus: variant.size_mapping_status,
      duplicateSize: variant.duplicate_size ? "true" : "false"
    }
  }

  #appendVariantDetails(cell, input, variant, { showPrice, showStockDate }) {
    if (variant.duplicate_size && variant.reference) {
      cell.append(this.#element("span", "max-w-24 text-[9px] leading-tight text-erp-muted", variant.reference))
    }
    if (showPrice && variant.customer_price_cents != null) {
      const price = this.#element("span", "text-[10px] font-bold tabular-nums", t("client_price", { price: this.#money(variant.customer_price_cents) }))
      price.dataset.textilePickerCellPrice = ""
      cell.append(price)
    }
    this.#appendMissingPrice(cell, input, variant)
    if (showStockDate && variant.stock_updated_at) {
      const stockDate = this.#element("span", "max-w-20 text-[9px] leading-tight text-erp-muted", t("stock_as_of", { date: this.#formatDate(variant.stock_updated_at) }))
      stockDate.dataset.textilePickerCellStockDate = ""
      cell.append(stockDate)
    }
  }

  #appendMissingPrice(cell, input, variant) {
    if (variant.customer_price_cents != null) return

    input.disabled = true
    const reason = this.#element("span", "max-w-20 text-[10px] leading-tight font-bold text-erp-danger",
      t(this.customerIdValue > 0 ? "price_unavailable" : "select_customer_first"))
    reason.id = `textile-picker-variant-${variant.variant_id}-reason`
    input.setAttribute("aria-describedby", reason.id)
    cell.append(reason)
  }

  #variantStockLabel(matrix, stock) {
    if (matrix.manual_entry) {
      return this.#element("span", "max-w-28 text-[10px] text-erp-muted", t("manual_stock"))
    }
    const stockClass = stock === 0 ? "font-bold text-erp-danger" : stock < LOW_STOCK ? "font-bold text-erp-st-pending-fg" : "text-erp-muted"
    const stockLabel = this.#element("span", `text-[11px] tabular-nums ${stockClass}`, stock === 0 ? t("out_of_stock") : t("stock", { count: stock }))
    stockLabel.dataset.stockLevel = stock === 0 ? "none" : stock < LOW_STOCK ? "low" : "ok"
    return stockLabel
  }

  #markQuantity(input) {
    const quantity = parseInt(input.value) || 0
    input.toggleAttribute("data-filled", quantity > 0)
    input.toggleAttribute("data-over", input.dataset.manualEntry !== "true" && quantity > Number(input.dataset.stock))
    const row = input.closest("tr")
    row?.toggleAttribute("data-has-quantity", [...row.querySelectorAll("input[data-variant-id]")].some(cell => (parseInt(cell.value) || 0) > 0))
  }

  // ── Selection ────────────────────────────────────────────────

  #catalogueState() {
    if (!this._matrix) return { valid: false, message: t("choose_product") }

    const colours = this.#catalogueColours()
    const pieces = colours.reduce((sum, colour) => sum + colour.sizes.reduce((total, size) => total + size.quantity, 0), 0)
    if (pieces === 0) return { valid: false, message: t("fill_quantities") }
    if (this._mode === "replace" && !this.#singleColourway(colours)) return { valid: false, message: t("select_one_colourway") }

    return { valid: true, message: this.#catalogueSummary(colours, pieces) }
  }

  #catalogueSummary(colours, pieces) {
    const totalCents = colours.reduce((sum, colour) => sum + colour.sizes.reduce((total, size) => total + size.quantity * (colour.unit_price_cents || 0), 0), 0)
    const colourCount = new Set(colours.map(colour => colour.product_colorway_id || colour.color_name)).size
    return t(this._matrix.manual_entry && this._matrix.article_kind === "object" ? "object_summary" : "catalogue_summary", {
      pieces: t("pieces", { count: pieces }),
      colours: t("colours", { count: colourCount }),
      unit: this._commonPrice != null ? formatCents(this._commonPrice) : "—",
      total: formatCents(totalCents)
    })
  }

  #customerState() {
    const pieces = this.#customSizes().reduce((sum, size) => sum + size.quantity, 0)
    const missing = [
      this.garmentInputTargets.some(input => input.checked) ? null : t("missing_garment_type"),
      pieces > 0 ? null : t("missing_quantity")
    ].filter(Boolean)
    this.sizeInputTargets.forEach(input => input.toggleAttribute("data-filled", (parseInt(input.value) || 0) > 0))
    if (missing.length > 0) return { valid: false, message: t("missing", { fields: missing.join(", ") }) }

    return { valid: true, message: t("customer_summary", { pieces: t("pieces", { count: pieces }) }) }
  }

  #singleColourway(colours) {
    return colours.length > 0 &&
      new Set(colours.map(colour => [colour.product_id, colour.product_colorway_id || colour.color_name].join("|"))).size === 1
  }

  // One entry per colourway and customer price. Sizes sharing a price stay on one textile line; a supplier size
  // with a distinct cost becomes another line so no price is averaged silently.
  #catalogueColours() {
    const byColour = new Map()

    this.matrixTarget.querySelectorAll("input[data-variant-id]:not([disabled])").forEach(input => {
      const quantity = parseInt(input.value) || 0
      if (quantity <= 0) return

      const key = this.#catalogueChoiceKey(input.dataset)
      if (!byColour.has(key)) byColour.set(key, this.#catalogueColour(input))
      byColour.get(key).sizes.push(this.#catalogueSize(input.dataset, quantity))
    })

    return [...byColour.values()]
  }

  #catalogueChoiceKey(data) {
    const collisionReference = data.duplicateSize === "true" ? data.reference : ""
    return [data.productColorwayId || data.colorName, data.customerPriceCents, data.supplierPriceCents, collisionReference].join("|")
  }

  #catalogueColour(input) {
    const data = input.dataset
    const compatibility = this.#quantityCompatibility(input)
    return {
      product_id: data.productId,
      product_colorway_id: data.productColorwayId || null,
      pricing_class: data.pricingClass,
      color_name: data.colorName,
      reference: data.reference,
      description: [data.productName, data.colorName, data.articleDetails].filter(Boolean).join(" — "),
      manual_entry: data.manualEntry === "true",
      supplier: data.supplier,
      image_url: data.imageUrl || null,
      compatibility,
      hexes: JSON.parse(input.closest("tr")?.dataset.hexes || "[]"),
      supplier_price_cents: data.supplierPriceCents === "" ? null : parseInt(data.supplierPriceCents),
      unit_price_cents: data.customerPriceCents === "" ? null : parseInt(data.customerPriceCents),
      sizes: []
    }
  }

  #quantityCompatibility(input) {
    try {
      return JSON.parse(input.closest("tr")?.dataset.compatibility || "{}")
    } catch {
      return {}
    }
  }

  #catalogueSize(data, quantity) {
    return {
      variant_id: data.variantId,
      size: data.canonicalSize || data.size,
      supplier_size: data.size,
      canonical_size_id: data.canonicalSizeId || null,
      reference: data.reference,
      stock: data.manualEntry === "true" ? null : Number(data.stock),
      quantity
    }
  }

  #customSizes() {
    return this.sizeInputTargets
      .map(input => ({ size_label: input.dataset.sizeLabel, quantity: parseInt(input.value) || 0 }))
      .filter(size => size.quantity > 0)
  }

  // ── DOM helpers ──────────────────────────────────────────────

  #element(tagName, className = "", text = null) {
    const element = document.createElement(tagName)
    if (className) element.className = className
    if (text != null) element.textContent = text
    return element
  }

  // Product image on a white backdrop, or a garment glyph when there is none or it fails to load
  #thumbnail(imageUrl, boxClasses, iconClasses) {
    const box = this.#element("span", `flex shrink-0 items-center justify-center overflow-hidden ${boxClasses}`)
    const placeholder = () => {
      const icon = this.#element("i", `lni lni-shirt-1 ${iconClasses} text-[#B9B09A]`)
      icon.setAttribute("aria-hidden", "true")
      icon.dataset.erpImagePlaceholder = ""
      box.replaceChildren(icon)
    }
    if (!imageUrl) {
      placeholder()
      return box
    }

    const img = this.#element("img", "size-full bg-white object-contain")
    img.alt = ""
    img.loading = "lazy"
    img.addEventListener("error", placeholder, { once: true })
    img.src = imageUrl
    box.append(img)
    return box
  }

  #money(cents) {
    return formatCents(cents).replace(/\s?€$/, "")
  }

  #formatDate(value) {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  }
}
